import React from "react";

/**
 * [PONPISH] 포인트 안내 (React + TailwindCSS)
 * - Tailwind 설정이 된 환경을 전제로 작성합니다.
 * - 좌측 고정 목차(>=lg), 모바일 접이식 목차, 섹션 앵커 스크롤, 가독성 위주의 레이아웃 구성.
 */

const sections = [
  {
    id: "usable",
    title: "사용가능 포인트란?",
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>PONPISH APP에서 상품 구매 및 서비스 이용 시 사용할 수 있는 포인트입니다.</li>
      </ul>
    ),
  },
  {
    id: "expiring",
    title: "소멸 예정 포인트란?",
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>포인트 유효기간 경과로 인해 곧 소멸될 포인트입니다.</li>
        <li>포인트 사용 시 유효기간이 짧은 포인트부터 우선 차감되어, 포인트 소멸을 최소화할 수 있습니다.</li>
      </ul>
    ),
  },
  {
    id: "earn-use",
    title: "포인트 적립 및 사용 안내",
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>포인트는 PONPISH 회원에게만 적립 및 사용됩니다.</li>
        <li>상품별 주문금액 기준으로 상품 구매 시, 구매 확정 시, 리뷰 작성 시(텍스트/포토) 즉시 적립됩니다.</li>
        <li>포인트 적립일로부터 1년간 유효하며, 기한 내 사용하지 않은 포인트는 소멸됩니다.</li>
        <li>결제 후 주문 취소 시, 포인트 사용기간이 남아 있으면 포인트가 복원됩니다.</li>
        <li>교환, 취소, 환불 시 발생하는 수수료 결제에도 포인트 사용이 가능합니다. 단, 반품 시 차감 금액은 포인트에서 우선 차감된 후, 잔여 금액이 환불됩니다.</li>
        <li>포인트를 사용한 상품이 취소 또는 반품 완료될 경우, 사용된 포인트는 복원됩니다.</li>
        <li>포인트는 모든 결제에 사용 가능하며, 최소 사용 가능 포인트는 1,000원부터입니다.</li>
      </ul>
    ),
  },
  {
    id: "etc",
    title: "기타 사항",
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>PONPISH 회원 탈퇴 시 보유하고 있던 모든 포인트는 소멸됩니다.</li>
        <li>본 포인트 정책은 PONPISH 운영 정책에 따라 변경될 수 있습니다.</li>
      </ul>
    ),
  },
];

function Toc({ sections }: { sections: { id: string; title: string }[] }) {
  return (
    <nav className="hidden lg:block sticky top-24 h-[calc(100vh-6rem)] overflow-auto pr-6">
      <h2 className="text-sm font-semibold text-black mb-3">목차</h2>
      <ol className="space-y-2 text-sm">
        {sections.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              className="block rounded-md px-2 py-1 hover:bg-gray-100 transition text-black"
            >
              {s.title}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function MobileToc({ sections }: { sections: { id: string; title: string }[] }) {
  return (
    <details className="lg:hidden mb-4 rounded-xl border bg-white shadow-sm">
      <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-black">목차 열기</summary>
      <div className="border-t px-4 py-3">
        <ol className="space-y-2 text-sm">
          {sections.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`} className="block rounded-md px-2 py-1 hover:bg-gray-100 text-black">
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      </div>
    </details>
  );
}

export default function PonpishPointGuidePage() {
  return (
    <div className="min-h-screen bg-white">
      {/* 컨테이너 */}
      <main className="mx-auto max-w-6xl px-2 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-8">
          <Toc sections={sections.map(({ id, title }) => ({ id, title }))} />

          <article className="min-w-0">
            <div className="rounded-2xl border bg-white p-6">
              <h1 className="text-2xl font-bold tracking-tight text-black">[PONPISH] 포인트 안내</h1>
              <p className="mt-2 mb-4 text-sm text-gray-600">시행일: 2025년 8월 1일</p>

              <MobileToc sections={sections.map(({ id, title }) => ({ id, title }))} />

              <div className="divide-y">
                {sections.map((s) => (
                  <section id={s.id} key={s.id} className="scroll-mt-24 py-6">
                    <h2 className="text-lg font-semibold mb-3 text-black">{s.title}</h2>
                    <div className="space-y-3 leading-7 text-black">{s.body}</div>
                  </section>
                ))}
              </div>

              <footer className="mt-6 border-t pt-4 text-xs text-gray-600">
                법령·서비스 변경에 따라 본 문서는 갱신될 수 있습니다.
              </footer>
            </div>
          </article>
        </div>
      </main>
    </div>
  );
}
